/* ==========================================================================
   TJÅKKO size rule: foot length in, size out, from one product's own table.

   The one copy of the rule. Two callers:
   - the product page finder "Mät din fot" (assets/tj-pdp.js, initFinder)
   - the store wide "Hitta din storlek" wizard (assets/tj-size-wizard.js)
   Both read the table JSON that snippets/tj-size-table.liquid prints, so for
   the same product and the same foot they give the same size.

   The rule (verified at 11 foot lengths, 2026-09-24, and in
   outputs/tjakko/mockups/pdp-templates/qa/size-audit/SIZE-FINDER-AUDIT.md):
   - the smallest size whose inner length is at least foot + ruleMin (1,0 cm)
   - a column named like "Fotlängd" is the brand's own foot range per size and
     decides instead, with no 1 cm rule
   - a table whose length column holds the width (every row under 15 cm while
     the next column sits within 2 cm of EU size / 1,5) is read swapped
   - a foot longer than the last row is "short", a foot far under the first
     row is "long"

   Loaded twice on a product page (header and tj-pdp-assets) on purpose: the
   second load is a no op.
   ========================================================================== */
(function () {
  'use strict';

  if (window.TJSizeRule) return;

  function num(value) {
    if (value === null || value === undefined) return NaN;
    var s = String(value).replace(',', '.').replace(/[^0-9.\-]/g, '');
    return parseFloat(s);
  }

  /* "20/21", "20-21", "20 21" and the same with an en dash (U+2013) are one size: KEEN names the size
     "20/21" in its table and "20-21" on the variant. */
  function normSize(raw) {
    return String(raw == null ? '' : raw)
      .trim()
      .toLowerCase()
      .replace(/\s*[\/\-\u2013]\s*/g, '/')
      .replace(/\s+/g, '/')
      .replace(/,/g, '.');
  }

  function sameSize(a, b) {
    return normSize(a) === normSize(b);
  }

  /* "25.0-25.6", "25,0 - 25,6", an en dash (U+2013) between the two, or a lone "25.3" */
  function footRange(value) {
    var parts = String(value == null ? '' : value)
      .replace(/&quot;|"/g, '')
      .replace(/,/g, '.')
      .split(/\s*[\-\u2013]\s*/)
      .map(function (part) { return parseFloat(part); })
      .filter(function (n) { return !isNaN(n); });
    if (!parts.length) return null;
    return { lo: Math.min.apply(null, parts), hi: Math.max.apply(null, parts) };
  }

  /* The cm a shopper types: "24,5", "24.5", " 24,5 cm". */
  function parseFoot(raw) {
    var s = String(raw == null ? '' : raw).replace(',', '.').trim();
    if (!s) return NaN;
    return parseFloat(s);
  }

  /* The input range the product page uses: a child's foot from 8 cm. */
  function bounds(child) {
    return child ? { min: 8, max: 30 } : { min: 15, max: 40 };
  }

  /* table: {cols:[...], rows:[[...], ...]} as printed by tj-size-table.
     opts.sizeCol / opts.lenCol: the template's columns (0 and 1 on every
     template today). Returns null when no row carries a usable length. */
  function prepare(table, opts) {
    opts = opts || {};
    if (!table || !table.rows || !table.rows.length) return null;

    var sizeCol = typeof opts.sizeCol === 'number' ? opts.sizeCol : 0;
    var lenCol = typeof opts.lenCol === 'number' ? opts.lenCol : 1;
    var cols = table.cols || [];

    var altCol = lenCol === 1 ? 2 : -1;
    var swapped = false;
    if (altCol > -1) {
      swapped = table.rows.length > 0 && table.rows.every(function (cells) {
        var len = num(cells[lenCol]);
        var other = num(cells[altCol]);
        var eu = parseFloat(String(cells[sizeCol] || '').replace(',', '.'));
        return !isNaN(len) && len < 15 && !isNaN(other) && !isNaN(eu) &&
          Math.abs(other - eu / 1.5) <= 2;
      });
    }
    if (swapped) {
      var held = lenCol;
      lenCol = altCol;
      altCol = held;
    }

    var footCol = -1;
    for (var f = 0; f < cols.length; f++) {
      if (/fotl[äa]ngd/i.test(String(cols[f]))) {
        footCol = f;
        break;
      }
    }

    var extraCol = -1;
    for (var c = 0; c < Math.max(cols.length, (table.rows[0] || []).length); c++) {
      if (c !== sizeCol && c !== lenCol && c !== footCol) {
        extraCol = c;
        break;
      }
    }
    /* when swapped, the values in the old length column are widths, and the
       name that describes them is the header of the column now read as length */
    var extraName = '';
    if (extraCol > -1) {
      extraName = String((swapped && extraCol === altCol ? cols[lenCol] : cols[extraCol]) || '');
    }

    var rows = table.rows
      .map(function (cells) {
        return {
          size: String(cells[sizeCol] || '').trim(),
          inner: num(cells[lenCol]),
          range: footCol > -1 ? footRange(cells[footCol]) : null,
          cells: cells
        };
      })
      .filter(function (row) {
        return row.size !== '' && !isNaN(row.inner);
      })
      .sort(function (a, b) {
        return a.inner - b.inner;
      });

    var byFoot = footCol > -1 && rows.filter(function (row) { return !!row.range; }).length >= 2;
    if (byFoot) {
      rows = rows.filter(function (row) { return !!row.range; });
    }
    if (!rows.length) return null;

    return {
      rows: rows,
      byFoot: byFoot,
      swapped: swapped,
      sizeCol: sizeCol,
      lenCol: lenCol,
      footCol: footCol,
      extraCol: extraCol,
      extraName: extraName
    };
  }

  /* value: foot length in cm (a number). Returns
     { status: 'ok', pick, below, room }  or
     { status: 'short', edge }  (the model ends below the foot) or
     { status: 'long', edge }   (the model starts far above the foot) */
  function pick(prepared, value, opts) {
    opts = opts || {};
    var ruleMin = typeof opts.ruleMin === 'number' && !isNaN(opts.ruleMin) ? opts.ruleMin : 1.0;
    var ruleMax = typeof opts.ruleMax === 'number' && !isNaN(opts.ruleMax) ? opts.ruleMax : 1.5;
    var rows = prepared.rows;
    var byFoot = prepared.byFoot;

    var hit = null;
    var below = null;
    /* by the brand's foot range: the first size whose range reaches the
       foot, so a foot that falls between two ranges (25,65 between 25,0 to
       25,6 and 25,7 to 26,2) takes the larger size */
    for (var i = 0; i < rows.length; i++) {
      var fits = byFoot
        ? value <= rows[i].range.hi + 0.001
        : rows[i].inner >= value + ruleMin;
      if (fits) {
        hit = rows[i];
        below = rows[i - 1] || null;
        break;
      }
    }

    if (!hit) return { status: 'short', edge: rows[rows.length - 1] };

    var tooLong = byFoot
      ? rows[0].range.lo - value > 0.5
      : rows[0].inner - value > ruleMax + 0.5;
    if (tooLong) return { status: 'long', edge: rows[0] };

    return { status: 'ok', pick: hit, below: below, room: hit.inner - value };
  }

  window.TJSizeRule = {
    num: num,
    normSize: normSize,
    sameSize: sameSize,
    footRange: footRange,
    parseFoot: parseFoot,
    bounds: bounds,
    prepare: prepare,
    pick: pick
  };
})();
